import React, { useState, useEffect } from 'react';
import { 
  Container, 
  Row, 
  Col, 
  Table, 
  Button, 
  Form, 
  InputGroup, 
  Modal, 
  Card, 
  Badge, 
  Alert, 
  Spinner 
} from 'react-bootstrap';
import Sidebar from '../components/Sidebar';
import Header from '../components/Header';
import axios from 'axios';
import { 
  BsSearch, 
  BsSortDown, 
  BsSortUp, 
  BsCheckCircle, 
  BsXCircle, 
  BsCashCoin, 
  BsPencilSquare 
} from 'react-icons/bs';
import { API_ENDPOINTS } from '../config';

const BillingPage = () => {
  const [billingDetails, setBillingDetails] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState('providerId');
  const [sortDirection, setSortDirection] = useState('asc');
  const [errorMessage, setErrorMessage] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [selectedRequestId, setSelectedRequestId] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchBillingDetails();
  }, []);

  const fetchBillingDetails = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_ENDPOINTS.getAllBillings}`, {
        headers: { token }
      });
      if (response.data.status) {
        setBillingDetails(response.data.allBillings || []);
      } else {
        //setErrorMessage('Error fetching billings: ' + response.data.message);
        setErrorMessage('');
      }
    } catch (error) {
      //setErrorMessage('Error fetching billing details: ' + error.message);
      setErrorMessage('');
    } finally {
      setLoading(false);
    }
  };
  
  const sortBillingDetails = (field) => {
    if (field === sortField) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const sortedAndFilteredBillingDetails = billingDetails
    .filter(billing =>
      billing.providerId.toLowerCase().includes(searchTerm.toLowerCase()) ||
      billing.providerName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      billing.providerRole.toLowerCase().includes(searchTerm.toLowerCase())
    )
    .sort((a, b) => {
      if (a[sortField] < b[sortField]) return sortDirection === 'asc' ? -1 : 1;
      if (a[sortField] > b[sortField]) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

  const handleUpdateBillingAmount = async (bookingId) => {
    const newAmount = prompt('Enter the new billing amount:');
    if (newAmount && !isNaN(newAmount) && newAmount >= 0) {
      try {
        const token = localStorage.getItem('token');
        await axios.patch(API_ENDPOINTS.updateBillingAmount, 
          { billingAmount: newAmount },
          { headers: { token, bookingid: bookingId } }
        );
        fetchBillingDetails();
      } catch (error) {
        setErrorMessage('Error updating billing amount: ' + error.message);
      }
    } else {
      setErrorMessage('Please enter a valid non-negative billing amount.');
    }
  };

  const handleWithdrawalRequest = async (providerId) => {
    if (window.confirm('Are you sure you want to request a withdrawal?')) {
      try {
        const token = localStorage.getItem('token');
        await axios.post(API_ENDPOINTS.withdrawalRequest, 
          {}, 
          { headers: { token, providerid: providerId } }
        );
        fetchBillingDetails();
      } catch (error) {
        setErrorMessage('Error processing withdrawal request: ' + error.message);
      }
    }
  };

  const handleUpdateWithdrawalStatus = async (requestId, newStatus) => {
    try {
      const token = localStorage.getItem('token');
      await axios.patch(API_ENDPOINTS.updateWithdrawalRequestStatus, 
        { status: newStatus },
        { headers: { token, requestId } }
      );
      fetchBillingDetails();
      setShowModal(false);
    } catch (error) {
      setErrorMessage('Error updating withdrawal status: ' + error.message);
    }
  };

  const handleShowModal = (requestId) => {
    setSelectedRequestId(requestId);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
  };

  return (
    <div className="d-flex">
      <Sidebar style={{ position: 'fixed', top: 0, bottom: 0, left: 0, width: '250px', zIndex: 1000 }} />
      <div style={{  width: 'calc(100%)', height: 'calc(100vh)', overflowY: 'auto' }}>
        <Header style={{ position: 'fixed', top: 0, left: '250px', width: 'calc(100% - 250px)', zIndex: 1000 }} />
        <Container fluid className="mt-4">
          <Card className="shadow-sm">
            <Card.Body>
              <h2 className="mb-4">Billing Details</h2>
              <Row className="mb-3">
                <Col md={6}>
                  <InputGroup>
                    <InputGroup.Text><BsSearch /></InputGroup.Text>
                    <Form.Control
                      placeholder="Search billing details..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </InputGroup>
                </Col>
              </Row>
              {errorMessage && <Alert variant="danger">{errorMessage}</Alert>}
              {loading ? (
                <div className="text-center my-5">
                  <Spinner animation="border" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </Spinner>
                </div>
              ) : (
                <Table responsive hover className="align-middle">
                  <thead className="bg-light">
                    <tr>
                      <th onClick={() => sortBillingDetails('providerId')}>
                        Provider ID {sortField === 'providerId' && (sortDirection === 'asc' ? <BsSortUp /> : <BsSortDown />)}
                      </th>
                      <th onClick={() => sortBillingDetails('providerName')}>
                        Name {sortField === 'providerName' && (sortDirection === 'asc' ? <BsSortUp /> : <BsSortDown />)}
                      </th>
                      <th onClick={() => sortBillingDetails('providerRole')}>
                        Role {sortField === 'providerRole' && (sortDirection === 'asc' ? <BsSortUp /> : <BsSortDown />)}
                      </th>
                      <th>Total Bookings</th>
                      <th>Billing Amount</th>
                      <th>Withdrawn Amount</th>
                      <th>Available for Withdrawal</th>
                      <th>Withdrawal Requests</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedAndFilteredBillingDetails.length > 0 ? (
                      sortedAndFilteredBillingDetails.map((billing) => {
                        const availableWithdrawal = Math.max(0, billing.billingAmount - billing.withdrawnAmount);
                        return (
                          <tr key={billing.providerId}>
                            <td>{billing.providerId}</td>
                            <td>{billing.providerName}</td>
                            <td><Badge bg="info">{billing.providerRole}</Badge></td>
                            <td>{billing.totalBookingCount}</td>
                            <td>${billing.billingAmount.toFixed(2)}</td>
                            <td>${billing.withdrawnAmount.toFixed(2)}</td>
                            <td>${availableWithdrawal.toFixed(2)}</td>
                            <td>
                              {billing.withdrawalRequests && billing.withdrawalRequests.length > 0 ? (
                                billing.withdrawalRequests
                                  .filter(request => request.status === 'Pending')
                                  .map((request, index) => (
                                    <div key={index} className="mb-2">
                                      <small>{`${new Date(request.requestDate).toLocaleDateString()} - $${request.amount.toFixed(2)}`}</small>
                                      <Button 
                                        variant="outline-success" 
                                        size="sm" 
                                        className="ms-2" 
                                        onClick={() => handleShowModal(request.requestId)}
                                      >
                                        <BsCheckCircle />
                                      </Button>
                                      <Button 
                                        variant="outline-danger" 
                                        size="sm" 
                                        className="ms-2" 
                                        onClick={() => handleShowModal(request.requestId)}
                                      >
                                        <BsXCircle />
                                      </Button>
                                    </div>
                                  ))
                              ) : (
                                <small className="text-muted">No requests</small>
                              )}
                            </td>
                            <td>
                              <Button variant="outline-warning" size="sm" className="me-2 mb-2" onClick={() => handleUpdateBillingAmount(billing.bookings[0].bookingId)}>
                                <BsPencilSquare /> Update Billing
                              </Button>
                              <Button variant="outline-primary" size="sm" onClick={() => handleWithdrawalRequest(billing.providerId)}>
                                <BsCashCoin /> Request Withdrawal
                              </Button>
                            </td>
                          </tr>
                        );
                      })
                    ) : (
                      <tr>
                        <td colSpan="9" className="text-center">No billing details found</td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              )}
            </Card.Body>
          </Card>
        </Container>
        <Modal show={showModal} onHide={handleCloseModal}>
          <Modal.Header closeButton>
            <Modal.Title>Update Withdrawal Request Status</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Button 
              variant="success" 
              className="me-2"
              onClick={() => handleUpdateWithdrawalStatus(selectedRequestId, 'Successful')}
            >
              <BsCheckCircle /> Mark as Successful
            </Button>
            <Button 
              variant="danger" 
              onClick={() => handleUpdateWithdrawalStatus(selectedRequestId, 'Failed')}
            >
              <BsXCircle /> Mark as Failed
            </Button>
          </Modal.Body>
        </Modal>
      </div>
    </div>
  );
};

export default BillingPage;